import Phaser from "phaser";
import { COPY } from "../config/brand";
import {
  CAMPAIGN_CLEAR_SETTLE_MS,
  CAMPAIGN_FINALE_MINIMUM_DURATION_MS,
  continuationSpawnPlan,
  type CampaignLevel,
} from "../config/campaign";
import {
  BOSS_START_MS,
  BOSS_WARNING_START_MS,
  DANGER_LINE_Y,
  ENEMIES,
  GAME_HEIGHT,
  GAME_WIDTH,
  PLAYER_SPEED,
  PLAYER_Y,
  POST_HIT_PROTECTION_MS,
  SPAWN_PLAN,
  TRIPLE_DURATION_MS,
  WEAPONS,
  type EnemyKind,
  type PowerUpKind,
  type WeaponKind,
} from "../config/game";
import { EVENT, emit, type HudDetail, type ResultDetail } from "./events";
import {
  activateHeldPowerUp,
  clearMilestone,
  collectPowerUp,
  createRunState,
  earnedTitle,
  fillStage,
  isTripleActive,
  mulberry32,
  registerClog,
  registerEscape,
  type RunState,
} from "./rules";
import { GameInput } from "./input";
import type { Preferences } from "./storage";

interface EnemyData {
  kind: EnemyKind;
  hits: number;
  hitPoints: number;
  progress: Phaser.GameObjects.Graphics;
  status: Phaser.GameObjects.Text | null;
  clogged: boolean;
  bossOpen: boolean;
  baseVelocityY: number;
}

export interface GameSession {
  mode: "quick" | "campaign";
  level: CampaignLevel | null;
  selectedWeapon: WeaponKind;
  comfyMode: boolean;
  bestScore: number;
  continued: boolean;
}

const QUICK_SESSION: GameSession = {
  mode: "quick",
  level: null,
  selectedWeapon: "poop",
  comfyMode: false,
  bestScore: 0,
  continued: false,
};

export class GameScene extends Phaser.Scene {
  private player!: Phaser.Physics.Arcade.Sprite;
  private enemies!: Phaser.Physics.Arcade.Group;
  private projectiles!: Phaser.Physics.Arcade.Group;
  private powerUps!: Phaser.Physics.Arcade.Group;
  private controls!: GameInput;
  private run: RunState = createRunState();
  private spawnIndex = 0;
  private activeElapsedMs = 0;
  private lastFrameAt = -1;
  private lastFire = 0;
  private bossSpawned = false;
  private bossWarningShown = false;
  private bossCountdown: Phaser.GameObjects.Text | null = null;
  private bossStateTimer: Phaser.Time.TimerEvent | null = null;
  private invulnerableUntil = 0;
  private currentWave: string = COPY.waves.one;
  private random = mulberry32(20260818);
  private preferences!: Preferences;
  private ended = false;
  private tripleExpiring = false;
  private tutorialContainer: Phaser.GameObjects.Container | null = null;
  private tutorialTimers: Phaser.Time.TimerEvent[] = [];
  private session: GameSession = QUICK_SESSION;
  private spawnPlan = SPAWN_PLAN;
  private bossStartMs = BOSS_START_MS;
  private campaignFinishScheduled = false;
  private campaignPowerSpawned = false;
  private campaignSecondPowerSpawned = false;
  private cleanedUp = false;

  constructor() {
    super("game");
  }

  create(): void {
    this.session =
      (this.game.registry.get("session") as GameSession | undefined) ??
      QUICK_SESSION;
    const level = this.session.level;
    this.run = createRunState({
      startingLives:
        (level?.startingLives ?? 3) + (this.session.comfyMode ? 1 : 0),
      startingWeapon:
        this.session.mode === "campaign" ? this.session.selectedWeapon : "poop",
    });
    this.ended = false;
    this.spawnIndex = 0;
    this.bossSpawned = false;
    this.bossWarningShown = false;
    this.bossCountdown = null;
    this.bossStateTimer = null;
    // Count only active gameplay frames. Phaser's absolute scene clock can jump
    // after a pause, tab switch, or long browser stall, which used to make every
    // overdue toilet spawn in one frame when play resumed.
    this.activeElapsedMs = 0;
    this.lastFrameAt = -1;
    this.lastFire = 0;
    this.invulnerableUntil = 0;
    this.tripleExpiring = false;
    this.campaignFinishScheduled = false;
    this.campaignPowerSpawned = false;
    this.campaignSecondPowerSpawned = false;
    this.cleanedUp = false;
    const baseSpawnPlan = level?.spawnPlan ?? SPAWN_PLAN;
    this.spawnPlan =
      level && this.session.continued
        ? continuationSpawnPlan(baseSpawnPlan)
        : baseSpawnPlan;
    this.bossStartMs = level?.bossAt ?? BOSS_START_MS;
    this.currentWave = level?.intro ?? COPY.waves.one;
    this.random = mulberry32(level?.seed ?? 20260818);
    this.preferences = this.game.registry.get("preferences") as Preferences;
    this.drawSpaceBathroom();

    this.enemies = this.physics.add.group();
    this.projectiles = this.physics.add.group();
    this.powerUps = this.physics.add.group();

    this.player = this.physics.add.sprite(GAME_WIDTH / 2, PLAYER_Y, "hero-art");
    this.player
      .setDisplaySize(112, 116)
      .setDepth(6)
      .setCollideWorldBounds(true);
    (this.player.body as Phaser.Physics.Arcade.Body)
      .setCircle(175, 73, 81)
      .setOffset(73, 81);

    this.physics.add.overlap(
      this.projectiles,
      this.enemies,
      this.onProjectileHit as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    );
    this.physics.add.overlap(
      this.player,
      this.powerUps,
      this.onPowerUp as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    );
    this.physics.add.overlap(
      this.player,
      this.enemies,
      this
        .onEnemyCollision as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    );

    this.controls = new GameInput(this, this.preferences);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanup, this);
    this.events.once(Phaser.Scenes.Events.DESTROY, this.cleanup, this);

    this.game.events.on("test:victory", this.forceVictory, this);
    this.game.events.on("test:defeat", this.forceDefeat, this);
    this.game.events.on("test:grant-triple", this.grantTriple, this);
    this.game.events.on("test:grant-shield", this.grantShield, this);
    this.game.events.on("test:force-collision", this.forceCollision, this);
    this.game.events.on("test:force-clogs", this.forceClogs, this);
    this.game.events.on("test:boss-warning", this.forceBossWarning, this);
    this.game.events.on("test:force-boss", this.forceBoss, this);
    this.game.events.on("test:force-boss-clog", this.forceBossClog, this);
    this.game.events.on("preferences:changed", this.onPreferences, this);
    this.game.events.on("power:activate", this.activateTriple, this);
    this.showTutorial();
    this.emitHud();
    emit(EVENT.announce, level?.intro ?? COPY.announcements.waveOne);
  }

  update(time: number, delta: number): void {
    if (this.ended) return;
    // A frame hitch must slow the game briefly, never fast-forward its schedule.
    if (this.lastFrameAt < 0) this.lastFrameAt = time;
    else {
      this.activeElapsedMs += Phaser.Math.Clamp(
        time - this.lastFrameAt,
        0,
        100,
      );
      this.lastFrameAt = time;
    }
    const activeTime = this.activeElapsedMs;
    this.updatePlayer(delta);
    if (this.controls.specialPressed()) this.activateTriple();
    this.updateSpawning(activeTime);
    this.updateEnemies(activeTime);
    this.updateProjectiles();
    if (activeTime - this.lastFire >= WEAPONS[this.run.weapon].fireInterval)
      this.fire(activeTime);
    this.maybeFinishCampaignLevel();
  }

  pauseGame(): void {
    if (!this.ended) this.scene.pause();
  }

  resumeGame(): void {
    if (!this.ended) this.scene.resume();
  }

  snapshot(): unknown {
    return {
      ...this.run,
      activeElapsedMs: this.activeElapsedMs,
      spawnIndex: this.spawnIndex,
      wave: this.currentWave,
      playerX: this.player?.x ?? GAME_WIDTH / 2,
      projectileYs: this.projectiles
        ?.getChildren()
        .filter((child) => (child as Phaser.Physics.Arcade.Sprite).active)
        .map((child) => (child as Phaser.Physics.Arcade.Sprite).y),
      projectileKinds: this.projectiles
        ?.getChildren()
        .filter((child) => (child as Phaser.Physics.Arcade.Sprite).active)
        .map((child) => (child as Phaser.Physics.Arcade.Sprite).texture.key),
      projectileDamages: this.projectiles
        ?.getChildren()
        .filter((child) => (child as Phaser.Physics.Arcade.Sprite).active)
        .map(
          (child) =>
            ((child as Phaser.Physics.Arcade.Sprite).getData("damage") as
              number | undefined) ?? 1,
        ),
      projectileWidths: this.projectiles
        ?.getChildren()
        .filter((child) => (child as Phaser.Physics.Arcade.Sprite).active)
        .map((child) => (child as Phaser.Physics.Arcade.Sprite).displayWidth),
      enemyYs: this.enemies
        ?.getChildren()
        .filter((child) => (child as Phaser.Physics.Arcade.Sprite).active)
        .map((child) => (child as Phaser.Physics.Arcade.Sprite).y),
      enemyBaseVelocityYs: this.enemies
        ?.getChildren()
        .filter((child) => (child as Phaser.Physics.Arcade.Sprite).active)
        .map((child) => {
          const enemy = child as Phaser.Physics.Arcade.Sprite;
          const data = enemy.getData("enemy") as EnemyData | undefined;
          return data?.baseVelocityY ?? 0;
        }),
      enemyTextures: this.enemies
        ?.getChildren()
        .filter((child) => (child as Phaser.Physics.Arcade.Sprite).active)
        .map((child) => (child as Phaser.Physics.Arcade.Sprite).texture.key),
      bossWarningShown: this.bossWarningShown,
      bossSpawned: this.bossSpawned,
      bossStatus: (() => {
        const boss = this.enemies
          ?.getChildren()
          .find(
            (child) =>
              (
                (child as Phaser.Physics.Arcade.Sprite).getData("enemy") as
                  EnemyData | undefined
              )?.kind === "boss",
          ) as Phaser.Physics.Arcade.Sprite | undefined;
        const data = boss?.getData("enemy") as EnemyData | undefined;
        return boss && data?.status?.active
          ? { bossY: boss.y, y: data.status.y, text: data.status.text }
          : null;
      })(),
      mode: this.session.mode,
      levelId: this.session.level?.id ?? null,
      levelSpeedMultiplier: this.session.level?.speedMultiplier ?? 1,
      comfyMode: this.session.comfyMode,
    };
  }

  private drawSpaceBathroom(): void {
    const palette = this.session.level?.palette;
    const background = this.add.graphics();
    background
      .fillGradientStyle(
        palette?.top ?? 0x160d2a,
        palette?.top ?? 0x160d2a,
        palette?.bottom ?? 0x301a4b,
        palette?.bottom ?? 0x301a4b,
      )
      .fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    const rng = mulberry32(42);
    for (let index = 0; index < 58; index += 1) {
      const x = rng() * GAME_WIDTH;
      const y = rng() * (DANGER_LINE_Y - 30);
      const radius = rng() * 2.2 + 0.6;
      background
        .fillStyle(
          index % 4 === 0 ? (palette?.accent ?? 0xffcf5c) : 0xffffff,
          0.35 + rng() * 0.5,
        )
        .fillCircle(x, y, radius);
    }
    background
      .fillStyle(palette?.accent ?? 0x71e4d0, 0.09)
      .fillRoundedRect(22, 112, GAME_WIDTH - 44, DANGER_LINE_Y - 145, 28);
    background
      .lineStyle(3, palette?.accent ?? 0x71e4d0, 0.3)
      .strokeRoundedRect(22, 112, GAME_WIDTH - 44, DANGER_LINE_Y - 145, 28);
    background
      .lineStyle(4, palette?.danger ?? 0xff6fa9, 0.84)
      .lineBetween(28, DANGER_LINE_Y, GAME_WIDTH - 28, DANGER_LINE_Y);
    this.add
      .text(GAME_WIDTH / 2, DANGER_LINE_Y - 20, COPY.ui.game.dangerLine, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "14px",
        fontStyle: "bold",
        color: "#ffb8d3",
        letterSpacing: 3,
      })
      .setOrigin(0.5);
  }

  private updatePlayer(delta: number): void {
    const velocity = this.controls.velocity(this.player.x, PLAYER_SPEED);

    this.player.setVelocityX(velocity);
    const lean = Phaser.Math.Clamp(velocity / PLAYER_SPEED, -1, 1) * 0.12;
    this.player.rotation = Phaser.Math.Linear(
      this.player.rotation,
      lean,
      Math.min(1, delta / 80),
    );
  }

  private updateSpawning(time: number): void {
    const elapsed = time;
    if (this.session.mode === "campaign") {
      while (
        this.spawnIndex < this.spawnPlan.length &&
        elapsed >= (this.spawnPlan[this.spawnIndex]?.at ?? Infinity)
      ) {
        const plan = this.spawnPlan[this.spawnIndex];
        if (plan) this.spawnEnemy(plan.kind, plan.lane, plan.motion);
        this.spawnIndex += 1;
      }
      const level = this.session.level;
      if (
        level &&
        !this.campaignPowerSpawned &&
        level.id >= 3 &&
        this.spawnIndex >= Math.ceil(this.spawnPlan.length * 0.45)
      ) {
        this.campaignPowerSpawned = true;
        this.spawnPowerUp(
          level.id === 4 || level.id === 8 ? "shield" : "triple",
          270,
        );
      }
      if (
        level &&
        level.id >= 9 &&
        !this.campaignSecondPowerSpawned &&
        this.spawnIndex >= Math.ceil(this.spawnPlan.length * 0.75)
      ) {
        this.campaignSecondPowerSpawned = true;
        this.spawnPowerUp("shield", level.id === 9 ? 390 : 150);
      }
      if (
        this.session.level?.bossAt !== null &&
        this.session.level?.bossAt !== undefined
      ) {
        const warningAt = Math.max(0, this.bossStartMs - 4_000);
        if (elapsed >= warningAt && !this.bossWarningShown && !this.bossSpawned)
          this.showBossCountdown();
        if (elapsed >= this.bossStartMs && !this.bossSpawned) this.spawnBoss();
      }
      return;
    }

    if (
      elapsed >= 27_500 &&
      elapsed < 59_500 &&
      this.currentWave !== COPY.waves.two
    ) {
      this.currentWave = COPY.waves.two;
      this.announceWave(this.currentWave);
      this.spawnPowerUp("shield", GAME_WIDTH / 2);
    } else if (
      elapsed >= 59_500 &&
      elapsed < BOSS_START_MS &&
      this.currentWave !== COPY.waves.three
    ) {
      this.currentWave = COPY.waves.three;
      this.announceWave(this.currentWave);
      this.spawnPowerUp("triple", 150);
    }

    while (
      this.spawnIndex < this.spawnPlan.length &&
      elapsed >= (this.spawnPlan[this.spawnIndex]?.at ?? Infinity)
    ) {
      const plan = this.spawnPlan[this.spawnIndex];
      if (plan) this.spawnEnemy(plan.kind, plan.lane, plan.motion);
      this.spawnIndex += 1;
    }

    if (
      elapsed >= BOSS_WARNING_START_MS &&
      !this.bossWarningShown &&
      !this.bossSpawned
    )
      this.showBossCountdown();

    if (elapsed >= BOSS_START_MS && !this.bossSpawned) this.spawnBoss();
  }

  private maybeFinishCampaignLevel(): void {
    if (
      this.session.mode !== "campaign" ||
      this.ended ||
      this.campaignFinishScheduled ||
      this.spawnIndex < this.spawnPlan.length ||
      (this.session.level?.bossAt !== null && !this.bossSpawned)
    )
      return;
    const activeThreat = this.enemies.getChildren().some((child) => {
      const sprite = child as Phaser.Physics.Arcade.Sprite;
      const data = sprite.getData("enemy") as EnemyData | undefined;
      return sprite.active && data && !data.clogged;
    });
    if (activeThreat) return;
    if (this.session.level?.bossAt !== null) return;
    this.campaignFinishScheduled = true;
    this.time.delayedCall(CAMPAIGN_CLEAR_SETTLE_MS, () => {
      if (!this.ended) this.finish(true);
    });
  }

  private updateEnemies(time: number): void {
    for (const child of this.enemies.getChildren()) {
      const enemy = child as Phaser.Physics.Arcade.Sprite;
      if (!enemy.active) continue;
      const data = enemy.getData("enemy") as EnemyData;
      if (data.clogged) continue;
      if (data.kind === "boss") {
        // The DOM HUD covers the top of the canvas. Keep the boss meter and
        // state label below the large toilet so both stay readable on phones.
        data.progress.setPosition(
          enemy.x,
          enemy.y + enemy.displayHeight * 0.52,
        );
        data.status?.setPosition(enemy.x, enemy.y + enemy.displayHeight * 0.66);
      } else {
        data.progress.setPosition(enemy.x, enemy.y - enemy.displayHeight * 0.6);
        data.status?.setPosition(enemy.x, enemy.y - enemy.displayHeight * 0.65);
      }
      if (data.kind === "boss") {
        const darkBoss = (this.session.level?.id ?? 0) >= 10;
        enemy.x =
          GAME_WIDTH / 2 +
          Math.sin(time / (darkBoss ? 900 : 1_100)) * (darkBoss ? 155 : 145);
        continue;
      }
      if (enemy.y - enemy.displayHeight / 2 > DANGER_LINE_Y)
        this.escapeEnemy(enemy);
    }
  }

  private updateProjectiles(): void {
    for (const child of this.projectiles.getChildren()) {
      const projectile = child as Phaser.Physics.Arcade.Sprite;
      if (projectile.y < -30) projectile.destroy();
    }
    for (const child of this.powerUps.getChildren()) {
      const power = child as Phaser.Physics.Arcade.Sprite;
      if (power.y > GAME_HEIGHT + 30) power.destroy();
    }
  }

  private fire(time: number): void {
    this.lastFire = time;
    const weapon = WEAPONS[this.run.weapon];
    const darkFlush = (this.session.level?.id ?? 0) >= 7;
    const visualScale = darkFlush && weapon.kind === "poop" ? 0.82 : 1;
    const angles = isTripleActive(this.run, time) ? [-0.16, 0, 0.16] : [0];
    angles.forEach((angle) => {
      const projectile = this.physics.add.sprite(
        this.player.x,
        this.player.y - 54,
        weapon.texture,
      );
      // Physics groups apply their defaults when a child is added. Add first,
      // then set motion so the group's zero-velocity default cannot erase it.
      this.projectiles.add(projectile);
      projectile
        .setDisplaySize(weapon.width * visualScale, weapon.height * visualScale)
        .setDepth(5)
        .setData("damage", weapon.damage);
      if (darkFlush && weapon.kind === "poop")
        projectile.setTint(0xffb0ef, 0x8dffd3, 0xffb0ef, 0x8dffd3);
      if (weapon.kind === "poop") projectile.body.setCircle(44, 20, 20);
      else if (weapon.kind === "bubble") projectile.body.setCircle(18, 6, 6);
      else projectile.body.setSize(24, 46).setOffset(8, 5);
      projectile.setVelocity(Math.sin(angle) * 420, -weapon.speed);
    });
    this.game.events.emit("audio:effect", "shoot");
  }

  private spawnEnemy(
    kind: Exclude<EnemyKind, "boss">,
    x: number,
    motion: "straight" | "diagonal" | "sway",
  ): void {
    if (this.ended) return;
    const definition = ENEMIES[kind];
    const darkFlush = (this.session.level?.id ?? 0) >= 7;
    const enemy = this.physics.add.sprite(
      x,
      130,
      `toilet-${kind}${darkFlush ? "-dark" : ""}`,
    );
    const baseVelocityY =
      definition.speed *
      (this.session.level?.speedMultiplier ?? 1) *
      (this.session.comfyMode ? 0.82 : 1);
    this.enemies.add(enemy);
    enemy
      .setDepth(4)
      .setDisplaySize(definition.width, definition.height)
      .setVelocityY(baseVelocityY)
      .setImmovable(true);
    enemy.body
      .setSize(enemy.width * 0.7, enemy.height * 0.72)
      .setOffset(enemy.width * 0.15, enemy.height * 0.13);
    if (motion === "diagonal")
      enemy
        .setVelocityX(this.random() > 0.5 ? 22 : -22)
        .setBounceX(1)
        .setCollideWorldBounds(true);
    if (motion === "sway") {
      this.tweens.add({
        targets: enemy,
        x: Phaser.Math.Clamp(
          x + (this.random() > 0.5 ? 90 : -90),
          70,
          GAME_WIDTH - 70,
        ),
        duration: 1_900,
        yoyo: true,
        repeat: -1,
        ease: "Sine.inOut",
      });
    }
    const progress = this.add.graphics().setDepth(7);
    const data: EnemyData = {
      kind,
      hits: 0,
      hitPoints: definition.hitPoints,
      progress,
      status: null,
      clogged: false,
      bossOpen: true,
      baseVelocityY,
    };
    enemy.setData("enemy", data);
    this.drawProgress(data);
    this.scheduleDarkFlushDash(enemy, data);
  }

  private scheduleDarkFlushDash(
    enemy: Phaser.Physics.Arcade.Sprite,
    data: EnemyData,
  ): void {
    const levelId = this.session.level?.id ?? 0;
    if (levelId < 7 || this.session.level?.bonus) return;
    const chance =
      levelId === 7
        ? data.kind === "speedy"
          ? 0.9
          : 0.28
        : levelId === 8
          ? 0.55
          : 0.78;
    if (this.random() > chance) return;
    const warningDelay = 1_500 + this.random() * 1_400;
    this.time.delayedCall(warningDelay, () => {
      if (!enemy.active || data.clogged || enemy.y > DANGER_LINE_Y - 190)
        return;
      const ring = this.add
        .circle(enemy.x, enemy.y, enemy.displayWidth * 0.62)
        .setStrokeStyle(4, 0xffcf5c, 0.95)
        .setDepth(8);
      enemy.setTint(0xffcf5c);
      if (!this.preferences.reducedMotion)
        this.tweens.add({
          targets: ring,
          scale: { from: 0.72, to: 1.14 },
          alpha: { from: 1, to: 0.25 },
          duration: 420,
        });
      this.time.delayedCall(430, () => {
        ring.destroy();
        if (!enemy.active || data.clogged) return;
        enemy.setTint(0xff4fd8);
        enemy.setVelocityY(
          data.baseVelocityY *
            (levelId >= 9 ? 1.3 : levelId === 8 ? 1.24 : 1.18),
        );
        this.time.delayedCall(650, () => {
          if (!enemy.active || data.clogged) return;
          enemy.setVelocityY(data.baseVelocityY);
          this.restoreEnemyDamageTint(enemy, data);
        });
      });
    });
  }

  private restoreEnemyDamageTint(
    enemy: Phaser.Physics.Arcade.Sprite,
    data: EnemyData,
  ): void {
    const stage = fillStage(data.hits, data.hitPoints);
    if (stage === 0) enemy.clearTint();
    else if (stage === 1) enemy.setTint(0xcdf9f1);
    else enemy.setTint(0x71e4d0);
  }

  private showBossCountdown(): void {
    if (this.bossWarningShown || this.bossSpawned || this.ended) return;
    this.bossWarningShown = true;
    this.currentWave = COPY.waves.finale;
    this.bossCountdown = this.add
      .text(GAME_WIDTH / 2, 205, `${COPY.boss.approaching} 4`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "27px",
        fontStyle: "bold",
        color: "#fff3d6",
        stroke: "#301a4b",
        strokeThickness: 7,
        align: "center",
      })
      .setOrigin(0.5)
      .setDepth(19);
    [3, 2, 1].forEach((seconds, index) => {
      this.time.delayedCall((index + 1) * 1_000, () => {
        if (!this.bossCountdown?.active || this.ended) return;
        this.bossCountdown
          .setText(`${COPY.boss.approaching} ${seconds}`)
          .setScale(1.16);
        this.tweens.add({
          targets: this.bossCountdown,
          scale: 1,
          duration: 220,
        });
      });
    });
    this.game.events.emit("audio:effect", "milestone");
    emit(EVENT.announce, COPY.announcements.bossApproaching);
    this.emitHud();
  }

  private spawnBoss(): void {
    this.stopBossStateTimer();
    this.bossSpawned = true;
    this.bossCountdown?.destroy();
    this.bossCountdown = null;
    this.currentWave = COPY.waves.boss;
    this.announceWave(COPY.bossIncoming);
    const enemy = this.physics.add.sprite(
      GAME_WIDTH / 2,
      220,
      (this.session.level?.id ?? 0) >= 10 ? "toilet-boss-dark" : "toilet-boss",
    );
    this.enemies.add(enemy);
    enemy
      .setDepth(4)
      .setDisplaySize(ENEMIES.boss.width, ENEMIES.boss.height)
      .setImmovable(true);
    enemy.body
      .setSize(enemy.width * 0.72, enemy.height * 0.72)
      .setOffset(enemy.width * 0.14, enemy.height * 0.14);
    const progress = this.add
      .graphics()
      .setDepth(7)
      .setPosition(enemy.x, enemy.y + enemy.displayHeight * 0.52);
    const status = this.add
      .text(enemy.x, enemy.y + enemy.displayHeight * 0.66, COPY.boss.closed, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "16px",
        fontStyle: "bold",
        color: "#d9c7ff",
        backgroundColor: "#301a4b",
        padding: { x: 9, y: 5 },
      })
      .setOrigin(0.5)
      .setDepth(9);
    const data: EnemyData = {
      kind: "boss",
      hits: 0,
      hitPoints: ENEMIES.boss.hitPoints,
      progress,
      status,
      clogged: false,
      bossOpen: false,
      baseVelocityY: 0,
    };
    enemy.setData("enemy", data);
    this.drawProgress(data);
    this.bossStateTimer = this.time.addEvent({
      delay: (this.session.level?.id ?? 0) >= 10 ? 1_450 : 1_650,
      loop: true,
      callback: () => {
        if (!enemy.active || !status.active || data.clogged || this.ended)
          return;
        data.bossOpen = !data.bossOpen;
        enemy.setTint(data.bossOpen ? 0xffffff : 0x8f86a7);
        enemy.setAlpha(data.bossOpen ? 1 : 0.86);
        status
          .setText(data.bossOpen ? COPY.boss.open : COPY.boss.closed)
          .setColor(data.bossOpen ? "#ffcf5c" : "#d9c7ff");
        if (!this.preferences.reducedMotion)
          this.tweens.add({
            targets: status,
            scale: { from: 1.14, to: 1 },
            duration: 220,
          });
        emit(
          EVENT.announce,
          data.bossOpen
            ? COPY.announcements.bossOpen
            : COPY.announcements.bossBlocking,
        );
      },
    });
  }

  private spawnPowerUp(kind: PowerUpKind, x: number): void {
    const power = this.physics.add.sprite(x, 180, `power-${kind}`);
    this.powerUps.add(power);
    power.setDepth(5).setVelocityY(72).setData("kind", kind);
    power.body.setCircle(25, 3, 3);
    this.tweens.add({
      targets: power,
      angle: 360,
      duration: 2_800,
      repeat: -1,
    });
  }

  private readonly onProjectileHit = (
    projectileObject: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    enemyObject: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ): void => {
    const projectile = projectileObject as Phaser.Physics.Arcade.Sprite;
    const enemy = enemyObject as Phaser.Physics.Arcade.Sprite;
    if (!projectile.active || !enemy.active) return;
    projectile.destroy();
    const data = enemy.getData("enemy") as EnemyData;
    if (data.clogged) return;
    if (data.kind === "boss" && !data.bossOpen) {
      this.game.events.emit("audio:effect", "shield");
      this.flashAt(enemy.x, enemy.y, 0x9d7de2);
      return;
    }

    const damage = (projectile.getData("damage") as number | undefined) ?? 1;
    data.hits += damage;
    this.game.events.emit("audio:effect", "hit");
    this.drawProgress(data);
    const stage = fillStage(data.hits, data.hitPoints);
    if (stage === 1) {
      enemy.setTint(0xcdf9f1);
    }
    if (stage === 2) {
      enemy.setTint(0x71e4d0);
      if (!this.preferences.reducedMotion)
        this.tweens.add({
          targets: enemy,
          angle: { from: -3, to: 3 },
          duration: 80,
          yoyo: true,
          repeat: 3,
        });
    }
    if (stage === 3) this.clogEnemy(enemy);
  };

  private readonly onPowerUp = (
    _player: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    powerObject: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ): void => {
    const power = powerObject as Phaser.Physics.Arcade.Sprite;
    if (!power.active) return;
    const kind = power.getData("kind") as PowerUpKind;
    this.run = collectPowerUp(this.run, kind);
    power.destroy();
    this.game.events.emit("audio:effect", "power");
    this.game.events.emit("audio:haptic", [20, 30, 20]);
    this.burst(
      this.player.x,
      this.player.y,
      kind === "shield" ? 0x71e4d0 : 0xffcf5c,
      14,
    );
    emit(
      EVENT.announce,
      kind === "shield"
        ? COPY.announcements.shieldReady
        : COPY.announcements.tripleReady,
    );
    this.emitHud();
  };

  private readonly onEnemyCollision = (
    _player: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    enemyObject: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ): void => {
    const enemy = enemyObject as Phaser.Physics.Arcade.Sprite;
    if (!enemy.active || this.ended) return;
    this.escapeEnemy(enemy, true);
  };

  private clogEnemy(enemy: Phaser.Physics.Arcade.Sprite): void {
    const data = enemy.getData("enemy") as EnemyData;
    data.clogged = true;
    if (data.kind === "boss") this.stopBossStateTimer();
    const result = registerClog(this.run, data.kind, {
      allowWeaponProgression: this.session.mode === "quick",
    });
    this.run = result.state;
    (enemy.body as Phaser.Physics.Arcade.Body).enable = false;
    data.progress.destroy();
    const clogLabel = this.add
      .text(enemy.x, enemy.y, "CLOGGED!", {
        fontFamily: "system-ui, sans-serif",
        fontSize: data.kind === "boss" ? "25px" : "17px",
        fontStyle: "bold",
        color: "#fff3d6",
        stroke: "#301a4b",
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(9);
    this.game.events.emit(
      "audio:effect",
      data.kind === "boss" ? "victory" : "clog",
    );
    this.game.events.emit(
      "audio:haptic",
      data.kind === "boss" ? [30, 40, 60] : 25,
    );
    this.burst(enemy.x, enemy.y, 0x71e4d0, data.kind === "boss" ? 34 : 16);

    if (result.weaponAwarded)
      this.celebrateWeaponUpgrade(result.weaponAwarded, result.lifeAwarded);
    else if (result.milestone) this.celebrateMilestone(result.milestone);
    else if (result.lifeAwarded) this.celebrateExtraLife();
    this.emitHud();

    if (data.kind === "boss") {
      clogLabel.destroy();
      this.backwardFlush(enemy);
      return;
    }

    const exitScaleX = enemy.scaleX * 0.35;
    const exitScaleY = enemy.scaleY * 0.35;
    this.tweens.add({
      targets: [enemy, clogLabel],
      y: -180,
      angle: this.preferences.reducedMotion ? 0 : 720,
      scaleX: exitScaleX,
      scaleY: exitScaleY,
      duration: this.preferences.reducedMotion ? 300 : 720,
      ease: "Back.in",
      onComplete: () => {
        enemy.destroy();
        clogLabel.destroy();
      },
    });
  }

  private escapeEnemy(
    enemy: Phaser.Physics.Arcade.Sprite,
    collided = false,
  ): void {
    const data = enemy.getData("enemy") as EnemyData;
    if (data.clogged) return;
    data.clogged = true;
    data.progress.destroy();
    data.status?.destroy();
    enemy.destroy();
    if (this.session.mode === "campaign" && this.session.level?.bonus) {
      this.game.events.emit("audio:effect", "clog");
      this.flushEffect(true);
      emit(EVENT.announce, "Bonus round! Runaway toilets cannot hurt you.");
      this.emitHud();
      return;
    }
    if (this.activeElapsedMs < this.invulnerableUntil) {
      this.flushEffect(true);
      emit(EVENT.announce, COPY.announcements.recovery);
      return;
    }
    const result = registerEscape(this.run);
    this.run = result.state;
    this.game.events.emit(
      "audio:effect",
      result.shieldConsumed ? "shield" : "escape",
    );
    this.game.events.emit(
      "audio:haptic",
      result.shieldConsumed ? 20 : [60, 50, 60],
    );
    this.flushEffect(result.shieldConsumed);
    this.emitHud();
    if (result.shieldConsumed)
      emit(EVENT.announce, COPY.announcements.shieldSaved);
    if (result.lifeLost) {
      if (collided) {
        this.flashAt(this.player.x, this.player.y, 0xff6fa9);
        this.burst(this.player.x, this.player.y - 15, 0xff6fa9, 18);
        emit(EVENT.announce, COPY.announcements.toiletCollision);
      }
      this.miniFlushSpin();
    }
    if (this.run.result === "defeat") this.finish(false);
  }

  private drawProgress(data: EnemyData): void {
    const width = data.kind === "boss" ? 120 : 64;
    const ratio = Phaser.Math.Clamp(data.hits / data.hitPoints, 0, 1);
    data.progress
      .clear()
      .fillStyle(0x160d2a, 0.8)
      .fillRoundedRect(-width / 2, -5, width, 10, 5);
    if (ratio > 0)
      data.progress
        .fillStyle(ratio > 0.65 ? 0xffcf5c : 0x71e4d0)
        .fillRoundedRect(-width / 2 + 2, -3, (width - 4) * ratio, 6, 3);
  }

  private celebrateMilestone(message: string): void {
    this.game.events.emit("audio:effect", "milestone");
    this.game.events.emit("audio:haptic", [20, 30, 20, 30, 40]);
    const banner = this.add
      .text(GAME_WIDTH / 2, 320, message, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "27px",
        fontStyle: "bold",
        color: "#301a4b",
        backgroundColor: "#ffcf5c",
        padding: { x: 18, y: 12 },
        align: "center",
        wordWrap: { width: 420 },
      })
      .setOrigin(0.5)
      .setDepth(20);
    this.burst(GAME_WIDTH / 2, 310, 0xffcf5c, 38);
    emit(EVENT.announce, message);
    this.tweens.add({
      targets: banner,
      y: 285,
      alpha: 0,
      delay: 1_100,
      duration: 450,
      onComplete: () => banner.destroy(),
    });
    this.time.delayedCall(1_600, () => {
      this.run = clearMilestone(this.run);
      this.emitHud();
    });
  }

  private celebrateExtraLife(): void {
    this.game.events.emit("audio:effect", "power");
    this.game.events.emit("audio:haptic", [25, 35, 60]);
    this.burst(GAME_WIDTH / 2, 210, 0xff6fa9, 30);
    emit(EVENT.announce, COPY.announcements.bonusLife);
  }

  private celebrateWeaponUpgrade(
    weapon: Exclude<WeaponKind, "poop">,
    lifeAwarded: boolean,
  ): void {
    const bubble = weapon === "bubble";
    const message = bubble
      ? COPY.announcements.bubbleUnlocked
      : lifeAwarded
        ? COPY.announcements.plungerUnlockedHeart
        : COPY.announcements.plungerUnlocked;
    const color = bubble ? 0x71e4d0 : 0xffcf5c;
    this.game.events.emit("audio:effect", "milestone");
    this.game.events.emit("audio:haptic", [30, 35, 30, 35, 70]);
    const banner = this.add
      .text(GAME_WIDTH / 2, 305, message, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "25px",
        fontStyle: "bold",
        color: "#301a4b",
        backgroundColor: bubble ? "#71e4d0" : "#ffcf5c",
        padding: { x: 18, y: 13 },
        align: "center",
        wordWrap: { width: 430 },
      })
      .setOrigin(0.5)
      .setDepth(22);
    this.burst(GAME_WIDTH / 2, 300, color, 46);
    emit(EVENT.announce, message);
    this.tweens.add({
      targets: banner,
      y: 270,
      alpha: 0,
      delay: 1_650,
      duration: 500,
      onComplete: () => banner.destroy(),
    });
    this.time.delayedCall(2_200, () => {
      this.run = clearMilestone(this.run);
      this.emitHud();
    });
  }

  private backwardFlush(enemy: Phaser.Physics.Arcade.Sprite): void {
    const ring = this.add
      .circle(enemy.x, enemy.y, 20, 0x71e4d0, 0.45)
      .setDepth(3);
    this.tweens.add({ targets: ring, radius: 650, alpha: 0, duration: 1_100 });
    const data = enemy.getData("enemy") as EnemyData;
    data.status?.destroy();
    this.tweens.add({
      targets: enemy,
      y: -220,
      angle: this.preferences.reducedMotion ? 0 : -1_080,
      scale: 0.1,
      duration: this.preferences.reducedMotion ? 450 : 1_150,
      ease: "Back.in",
      onComplete: () => {
        enemy.destroy();
        const minimumDelay =
          this.session.mode === "campaign"
            ? Math.max(
                0,
                CAMPAIGN_FINALE_MINIMUM_DURATION_MS - this.activeElapsedMs,
              )
            : 0;
        this.time.delayedCall(minimumDelay, () => this.finish(true));
      },
    });
    emit(EVENT.announce, COPY.announcements.backwardFlush);
  }

  private miniFlushSpin(): void {
    if (this.activeElapsedMs < this.invulnerableUntil) return;
    this.invulnerableUntil = this.activeElapsedMs + POST_HIT_PROTECTION_MS;
    this.player.setAlpha(0.48);
    const angle = this.preferences.reducedMotion ? 0 : 360;
    this.tweens.add({
      targets: this.player,
      angle,
      duration: this.preferences.reducedMotion ? 180 : 520,
      ease: "Sine.inOut",
      onComplete: () => this.player.setAngle(0),
    });
    this.time.delayedCall(POST_HIT_PROTECTION_MS, () =>
      this.player.setAlpha(1),
    );
  }

  private flushEffect(shielded: boolean): void {
    const color = shielded ? 0x71e4d0 : 0xff6fa9;
    const wave = this.add
      .rectangle(GAME_WIDTH / 2, DANGER_LINE_Y, GAME_WIDTH, 8, color, 0.8)
      .setDepth(8);
    this.tweens.add({
      targets: wave,
      scaleY: 11,
      alpha: 0,
      duration: 480,
      onComplete: () => wave.destroy(),
    });
  }

  private burst(x: number, y: number, color: number, count: number): void {
    const emitter = this.add.particles(x, y, "spark", {
      speed: { min: 70, max: 260 },
      angle: { min: 200, max: 340 },
      lifespan: { min: 350, max: 850 },
      quantity: count,
      scale: { start: 1, end: 0 },
      tint: [color, 0xffffff, 0xffcf5c],
      emitting: false,
    });
    emitter.setDepth(12).explode(count);
    this.time.delayedCall(900, () => emitter.destroy());
  }

  private flashAt(x: number, y: number, color: number): void {
    const flash = this.add.circle(x, y, 16, color, 0.65).setDepth(10);
    this.tweens.add({
      targets: flash,
      radius: 55,
      alpha: 0,
      duration: 230,
      onComplete: () => flash.destroy(),
    });
  }

  private announceWave(label: string): void {
    const text = this.add
      .text(GAME_WIDTH / 2, 170, label, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "25px",
        fontStyle: "bold",
        color: "#fff3d6",
        stroke: "#301a4b",
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setDepth(15);
    this.tweens.add({
      targets: text,
      y: 145,
      alpha: 0,
      delay: 900,
      duration: 450,
      onComplete: () => text.destroy(),
    });
    emit(EVENT.announce, label);
    this.emitHud();
  }

  private showTutorial(): void {
    if (this.preferences.tutorialComplete) {
      this.game.events.emit("tutorial:visibility", false);
      return;
    }
    const panel = this.add
      .rectangle(0, 0, 430, 300, 0x160d2a, 0.94)
      .setStrokeStyle(4, 0x71e4d0);
    const text = this.add
      .text(0, -118, COPY.tutorial, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "21px",
        fontStyle: "bold",
        color: "#fff3d6",
        align: "center",
        wordWrap: { width: 375 },
      })
      .setOrigin(0.5);
    const demoToilet = this.add.image(0, -22, "toilet-regular");
    demoToilet.setDisplaySize(74, 111);
    const meterTrack = this.add
      .rectangle(0, 34, 112, 12, 0x301a4b)
      .setStrokeStyle(2, 0xfff3d6);
    const meterFill = this.add
      .rectangle(-54, 34, 108, 8, 0x71e4d0)
      .setOrigin(0, 0.5)
      .setScale(0, 1);
    const arrow = this.add
      .text(0, 104, "↔", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "52px",
        color: "#ffcf5c",
      })
      .setOrigin(0.5);
    const hint = this.add
      .text(0, 76, COPY.tutorialHint, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "16px",
        color: "#71e4d0",
      })
      .setOrigin(0.5);
    this.tutorialContainer = this.add
      .container(GAME_WIDTH / 2, 570, [
        panel,
        text,
        demoToilet,
        meterTrack,
        meterFill,
        hint,
        arrow,
      ])
      .setDepth(18);
    this.tweens.add({
      targets: arrow,
      x: { from: -65, to: 65 },
      duration: 900,
      yoyo: true,
      repeat: 2,
    });
    [700, 1_800, 2_900].forEach((delay, index) => {
      this.tutorialTimers.push(
        this.time.delayedCall(delay, () => {
          if (!this.tutorialContainer) return;
          const pellet = this.add.image((index - 1) * 46, 118, "pellet-art");
          pellet.setDisplaySize(30, 30);
          this.tutorialContainer.add(pellet);
          this.tweens.add({
            targets: pellet,
            x: 0,
            y: -8,
            duration: 430,
            ease: "Sine.in",
            onComplete: () => {
              pellet.destroy();
              meterFill.setScale((index + 1) / 3, 1);
              demoToilet.setTint(
                index === 0 ? 0xcdf9f1 : index === 1 ? 0x71e4d0 : 0xffffff,
              );
              if (index === 1 && !this.preferences.reducedMotion)
                this.tweens.add({
                  targets: demoToilet,
                  angle: { from: -4, to: 4 },
                  duration: 90,
                  yoyo: true,
                  repeat: 3,
                });
              if (index === 2)
                this.tweens.add({
                  targets: demoToilet,
                  y: -190,
                  angle: this.preferences.reducedMotion ? 0 : 360,
                  duration: this.preferences.reducedMotion ? 250 : 650,
                });
            },
          });
        }),
      );
    });
    this.game.events.once("tutorial:skip", this.finishTutorial, this);
    this.game.events.emit("tutorial:visibility", true);
    this.tutorialTimers.push(
      this.time.delayedCall(4_800, this.finishTutorial, [], this),
    );
  }

  private readonly finishTutorial = (): void => {
    if (!this.tutorialContainer) return;
    this.tutorialContainer.destroy(true);
    this.tutorialContainer = null;
    this.tutorialTimers.forEach((timer) => timer.remove(false));
    this.tutorialTimers = [];
    this.game.events.off("tutorial:skip", this.finishTutorial, this);
    this.game.events.emit("tutorial:visibility", false);
    this.game.events.emit("tutorial:complete");
  };

  private emitHud(): void {
    const powerUp =
      this.run.shield > 0
        ? COPY.powerUps.shield
        : this.run.heldPowerUp === "triple"
          ? COPY.powerUps.tripleReady
          : isTripleActive(this.run, this.activeElapsedMs)
            ? this.tripleExpiring
              ? COPY.powerUps.tripleEnding
              : COPY.powerUps.tripleActive
            : null;
    emit<HudDetail>(EVENT.hud, {
      ...this.run,
      wave: this.currentWave,
      powerUp,
    });
  }

  private finish(victory: boolean): void {
    if (this.ended) return;
    this.finishTutorial();
    this.ended = true;
    this.physics.pause();
    const bestScore = this.session.bestScore;
    const isBestScore = this.run.score > bestScore;
    emit<ResultDetail>(EVENT.result, {
      state: { ...this.run, result: victory ? "victory" : "defeat" },
      isBestScore,
      title: earnedTitle(
        { ...this.run, result: victory ? "victory" : "defeat" },
        isBestScore,
      ),
      mode: this.session.mode,
      levelId: this.session.level?.id ?? null,
      levelName: this.session.level?.name ?? "Quick Play",
      comfyMode: this.session.comfyMode,
    });
  }

  private readonly forceVictory = (): void => {
    if (this.ended || !this.scene.isActive()) return;
    const result = registerClog(this.run, "boss", {
      allowWeaponProgression: this.session.mode === "quick",
    });
    this.run = result.state;
    this.finish(true);
  };

  private readonly forceDefeat = (): void => {
    if (this.ended || !this.scene.isActive()) return;
    this.run = { ...this.run, lives: 0, result: "defeat" };
    this.emitHud();
    this.finish(false);
  };

  private readonly grantTriple = (): void => {
    if (this.ended || !this.scene.isActive()) return;
    this.run = collectPowerUp(this.run, "triple");
    this.emitHud();
  };

  private readonly grantShield = (): void => {
    if (this.ended || !this.scene.isActive()) return;
    this.run = collectPowerUp(this.run, "shield");
    this.emitHud();
  };

  private readonly forceCollision = (): void => {
    if (this.ended || !this.scene.isActive()) return;
    let enemy = this.enemies.getChildren().find((child) => {
      const sprite = child as Phaser.Physics.Arcade.Sprite;
      const data = sprite.getData("enemy") as EnemyData | undefined;
      return sprite.active && data?.kind !== "boss" && !data?.clogged;
    }) as Phaser.Physics.Arcade.Sprite | undefined;
    if (!enemy) {
      this.spawnEnemy("regular", this.player.x, "straight");
      enemy = this.enemies.getChildren().at(-1) as Phaser.Physics.Arcade.Sprite;
    }
    enemy.setPosition(this.player.x, this.player.y).setVelocity(0, 0);
  };

  private readonly forceClogs = (target: number): void => {
    if (this.ended || !this.scene.isActive()) return;
    const safeTarget = Phaser.Math.Clamp(Math.floor(target), 0, 34);
    while (this.run.clogs < safeTarget) {
      const result = registerClog(this.run, "regular", {
        allowWeaponProgression: this.session.mode === "quick",
      });
      this.run = result.state;
      if (result.weaponAwarded)
        this.celebrateWeaponUpgrade(result.weaponAwarded, result.lifeAwarded);
      else if (result.lifeAwarded) this.celebrateExtraLife();
    }
    this.emitHud();
  };

  private readonly forceBossWarning = (): void => {
    this.showBossCountdown();
  };

  private readonly forceBoss = (): void => {
    if (this.ended || !this.scene.isActive() || this.bossSpawned) return;
    this.spawnBoss();
  };

  private readonly forceBossClog = (): void => {
    if (this.ended || !this.scene.isActive()) return;
    const boss = this.enemies.getChildren().find((child) => {
      const sprite = child as Phaser.Physics.Arcade.Sprite;
      const data = sprite.getData("enemy") as EnemyData | undefined;
      return sprite.active && data?.kind === "boss" && !data.clogged;
    }) as Phaser.Physics.Arcade.Sprite | undefined;
    if (!boss) return;
    const data = boss.getData("enemy") as EnemyData;
    data.bossOpen = true;
    data.hits = data.hitPoints;
    this.drawProgress(data);
    this.clogEnemy(boss);
  };

  private stopBossStateTimer(): void {
    this.bossStateTimer?.remove(false);
    this.bossStateTimer = null;
  }

  private readonly onPreferences = (preferences: Preferences): void => {
    this.preferences = preferences;
    this.controls.setPreferences(preferences);
  };

  private readonly activateTriple = (): void => {
    const next = activateHeldPowerUp(
      this.run,
      this.activeElapsedMs,
      TRIPLE_DURATION_MS,
    );
    if (next === this.run) return;
    this.run = next;
    this.tripleExpiring = false;
    this.game.events.emit("audio:effect", "power");
    this.game.events.emit("audio:haptic", [20, 30, 20]);
    this.burst(this.player.x, this.player.y - 30, 0xffcf5c, 20);
    emit(EVENT.announce, COPY.announcements.tripleActivated);
    this.emitHud();
    this.time.delayedCall(TRIPLE_DURATION_MS - 1_500, () => {
      if (!isTripleActive(this.run, this.activeElapsedMs) || this.ended) return;
      this.tripleExpiring = true;
      this.game.events.emit("audio:effect", "shield");
      emit(EVENT.announce, COPY.announcements.tripleEnding);
      this.emitHud();
    });
    this.time.delayedCall(TRIPLE_DURATION_MS + 30, () => {
      this.tripleExpiring = false;
      this.emitHud();
    });
  };

  private readonly cleanup = (): void => {
    if (this.cleanedUp) return;
    this.cleanedUp = true;
    this.controls?.destroy();
    this.game.events.off("test:victory", this.forceVictory, this);
    this.game.events.off("test:defeat", this.forceDefeat, this);
    this.game.events.off("test:grant-triple", this.grantTriple, this);
    this.game.events.off("test:grant-shield", this.grantShield, this);
    this.game.events.off("test:force-collision", this.forceCollision, this);
    this.game.events.off("test:force-clogs", this.forceClogs, this);
    this.game.events.off("test:boss-warning", this.forceBossWarning, this);
    this.game.events.off("test:force-boss", this.forceBoss, this);
    this.game.events.off("test:force-boss-clog", this.forceBossClog, this);
    this.game.events.off("preferences:changed", this.onPreferences, this);
    this.game.events.off("power:activate", this.activateTriple, this);
    this.game.events.off("tutorial:skip", this.finishTutorial, this);
    if (this.enemies?.children) {
      for (const child of this.enemies.getChildren()) {
        const data = (child as Phaser.Physics.Arcade.Sprite).getData(
          "enemy",
        ) as EnemyData | undefined;
        data?.progress.destroy();
        data?.status?.destroy();
      }
    }
    if (this.tutorialContainer) {
      this.tutorialContainer.destroy(true);
      this.tutorialContainer = null;
    }
    this.tutorialTimers.forEach((timer) => timer.remove(false));
    this.tutorialTimers = [];
    this.bossCountdown?.destroy();
    this.bossCountdown = null;
    this.stopBossStateTimer();
  };
}
